'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import {
  Sparkles,
  ArrowRight,
  Clock,
  Calendar,
  MapPin,
  Phone,
} from 'lucide-react';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Badge } from '@kit/ui/badge';
import { cn } from '@kit/ui/utils';

import './styles.css';

const DEEM_ACCOUNT_ID = process.env.NEXT_PUBLIC_DEFAULT_ACCOUNT_ID;

interface BookingPackage {
  id: string;
  name: string;
  description: string | null;
  booking_count: number;
  price: number;
  original_price: number | null;
  slot_duration: number;
  validity_months: number;
  validity_days: number | null;
  color: string | null;
  features: string[];
  display_order: number;
  package_type: string;
  max_subscribers: number | null;
  player_pricing: Record<string, number> | null;
}

interface PackageSubscriberCount {
  current_subscribers: number;
  max_subscribers: number | null;
  remaining_slots: number | null;
}

const PACKAGE_COLORS = {
  gold: '#B8860B',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
};

// --- Hero Section ---
function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-20">
      <div className="absolute inset-0 bg-[#000C27]">
        <Image
          src="/landing-pages/deem-padel/images/figma-hero-bg.png"
          alt="Indoor padel court at Deem Padel Club Jeddah"
          fill
          className="object-cover opacity-60"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#000C27] via-[#000C27]/90 to-[#000C27]/70" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-[#B8EC17]/20 text-[#B8EC17] px-4 py-2 rounded-full mb-6">
            <Image
              src="/landing-pages/deem-padel/images/figma-icon-star.svg"
              alt="Star"
              width={16}
              height={16}
              className="w-4 h-4"
            />
            <span className="text-sm">Premium Indoor Padel Courts in Jeddah</span>
          </div>

          <h1 className="font-conthrax text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Where Champions
            <br />
            <span className="text-[#1679FF]">Play Padel</span>
            <br />
            <span className="sr-only">in Jeddah</span>
          </h1>

          <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl">
            Experience the thrill of padel at Deem Padel Club. Three state-of-the-art indoor courts, professional coaching, and a vibrant community await you.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link href="/book">
              <Button
                size="lg"
                className="bg-[#1679FF] hover:bg-[#1679FF]/90 text-white px-8 py-6 text-lg group font-conthrax"
              >
                Book Your Court
                <Image
                  src="/landing-pages/deem-padel/images/figma-icon-arrow.svg"
                  alt="Arrow"
                  width={20}
                  height={20}
                  className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform"
                />
              </Button>
            </Link>
            <a href="#about-section">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-[#B8EC17] text-[#B8EC17] hover:bg-[#B8EC17] hover:text-[#000C27] px-8 py-6 text-lg bg-transparent font-conthrax"
              >
                Learn More
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#B8EC17] mb-2 font-conthrax">3</div>
              <div className="text-sm text-white/60">Indoor Courts</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#B8EC17] mb-2 font-conthrax">7</div>
              <div className="text-sm text-white/60">Days Open</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-[#B8EC17] mb-2 font-conthrax">4.7</div>
              <div className="text-sm text-white/60">Rating</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- About Section ---
function AboutSection() {
  return (
    <section id="about-section" className="py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <Image
                src="/landing-pages/deem-padel/images/padel_balls.jpg"
                alt="Players enjoying padel at Deem Padel Club in Jeddah"
                width={600}
                height={500}
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#1679FF] text-white p-8 rounded-2xl shadow-xl max-w-xs">
              <div className="text-4xl font-bold mb-2 font-conthrax">135+</div>
              <div className="text-sm opacity-90">Happy Members Playing Weekly</div>
            </div>
          </div>

          <div>
            <div className="inline-block bg-[#1679FF]/10 text-[#1679FF] px-4 py-2 rounded-full mb-4">
              About Deem Padel
            </div>
            <h2 className="font-conthrax text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Your Premier Padel Destination in Jeddah
            </h2>
            <p className="text-lg text-[#969696] mb-6">
              Deem Padel Club is Jeddah&apos;s leading indoor padel facility, offering world-class courts and a welcoming atmosphere for players of all skill levels.
            </p>
            <p className="text-lg text-[#969696] mb-8">
              From beginners taking their first swing to seasoned professionals honing their skills, we provide the perfect environment to enjoy this exciting sport.
            </p>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#B8EC17]/20 rounded-xl flex items-center justify-center">
                  <Image
                    src="/landing-pages/deem-padel/images/figma-icon-coaching.svg"
                    alt="Coaching"
                    width={24}
                    height={24}
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Pro Coaching</h3>
                  <p className="text-sm text-[#969696]">Expert trainers available</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1679FF]/20 rounded-xl flex items-center justify-center">
                  <Image
                    src="/landing-pages/deem-padel/images/figma-icon-community.svg"
                    alt="Community"
                    width={24}
                    height={24}
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Community</h3>
                  <p className="text-sm text-[#969696]">Join our vibrant club</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Packages Section ---
function PackagesSection() {
  const supabase = useSupabase();
  const [selectedPackageType, setSelectedPackageType] = useState<string>('all');

  const { data: allPackages, isLoading } = useQuery({
    queryKey: ['homepage-packages', DEEM_ACCOUNT_ID],
    queryFn: async () => {
      let query = supabase
        .from('booking_packages')
        .select('*')
        .eq('status', 'active')
        .eq('is_internal', false)
        .order('display_order', { ascending: true });

      if (DEEM_ACCOUNT_ID) {
        query = query.eq('account_id', DEEM_ACCOUNT_ID);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BookingPackage[];
    },
  });

  const packages = allPackages
    ?.filter(
      (pkg) => selectedPackageType === 'all' || pkg.package_type === selectedPackageType,
    )
    .slice(0, 3);

  const { data: subscriberCounts } = useQuery({
    queryKey: ['package-subscriber-counts', packages?.map((p) => p.id)],
    queryFn: async () => {
      if (!packages) return {};

      const counts: Record<string, PackageSubscriberCount> = {};
      const packagesWithLimits = packages.filter((p) => p.max_subscribers !== null);

      await Promise.all(
        packagesWithLimits.map(async (pkg) => {
          const { data, error } = await supabase.rpc('get_package_subscriber_count', {
            p_package_id: pkg.id,
          });

          if (!error && data && data.length > 0 && data[0]) {
            counts[pkg.id] = data[0];
          }
        }),
      );

      return counts;
    },
    enabled: !!packages && packages.length > 0,
  });

  const calculateTotalSavings = (
    price: number,
    originalPrice: number | null,
  ) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return originalPrice - price;
  };

  if (isLoading || !packages || packages.length === 0) {
    return null;
  }

  const packageTypes = [
    { value: 'all', label: 'All' },
    { value: 'court', label: 'Court' },
    { value: 'trainer', label: 'Coach' },
    { value: 'kids_academy', label: 'Kids Academy' },
  ];

  return (
    <section id="packages-section" className="py-12 lg:py-20 bg-[#000C27]">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#B8EC17]/20 text-[#B8EC17] px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-medium">Premium Packages</span>
          </div>
          <h2 className="font-conthrax text-4xl sm:text-5xl font-bold text-white mb-4">
            Get More for Less
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Save money with our exclusive packages. Book multiple sessions and enjoy significant savings while improving your game.
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {packageTypes.map((type) => (
            <Button
              key={type.value}
              variant="outline"
              size="sm"
              onClick={() => setSelectedPackageType(type.value)}
              className={cn(
                'transition-all border-2',
                selectedPackageType === type.value
                  ? 'bg-[#1679FF] hover:bg-[#1679FF]/90 text-white border-[#1679FF] shadow-md'
                  : 'border-white/30 text-white hover:text-white hover:bg-white/10 hover:border-white/50 bg-transparent',
              )}
            >
              {type.label}
            </Button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {packages.map((pkg) => {
            const totalSavings = calculateTotalSavings(
              pkg.price,
              pkg.original_price,
            );
            const subscriberInfo = subscriberCounts?.[pkg.id];

            let bgColor = pkg.color;
            if (!bgColor) {
              if (pkg.name.toLowerCase().includes('gold')) {
                bgColor = PACKAGE_COLORS.gold;
              } else if (pkg.name.toLowerCase().includes('silver')) {
                bgColor = PACKAGE_COLORS.silver;
              } else if (pkg.name.toLowerCase().includes('bronze')) {
                bgColor = PACKAGE_COLORS.bronze;
              }
            }

            return (
              <Card
                key={pkg.id}
                className="relative overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 bg-white/5 backdrop-blur-sm border border-white/10 flex flex-col"
              >
                {bgColor && (
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{ backgroundColor: bgColor }}
                  />
                )}

                <CardContent className="relative p-8 space-y-6 flex flex-col flex-1">
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <h3 className="text-2xl font-bold text-white font-conthrax">{pkg.name}</h3>
                      {subscriberInfo && subscriberInfo.max_subscribers !== null && (
                        <Badge
                          variant={
                            subscriberInfo.remaining_slots === 0 ? 'destructive' : 'outline'
                          }
                          className="whitespace-nowrap text-xs border-white/20 text-white"
                        >
                          {subscriberInfo.remaining_slots === 0
                            ? 'Sold Out'
                            : `${subscriberInfo.remaining_slots} left`}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-white/60 capitalize">
                      {pkg.package_type.replace('_', ' ')} Package: {pkg.booking_count} Bookings
                    </p>
                  </div>

                  <div className="space-y-2">
                    {pkg.player_pricing && (
                      <p className="text-sm text-white/60">starting at</p>
                    )}
                    <div className="flex items-baseline gap-2">
                      <CurrencyIcon className="w-6 h-6 text-white" />
                      <span className="text-4xl font-bold text-white font-conthrax">
                        {pkg.price.toFixed(0)}
                      </span>
                    </div>
                    {totalSavings > 0 && (
                      <div className="flex items-center gap-1 text-[#B8EC17]">
                        <span className="text-sm font-medium">Total savings</span>
                        <CurrencyIcon className="w-4 h-4" />
                        <span className="text-sm font-bold">
                          {totalSavings.toFixed(0)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-3 text-sm text-white">
                      <Clock className="w-5 h-5 text-[#1679FF]" />
                      <span className="font-medium">
                        {pkg.slot_duration} minutes per session
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-white">
                      <Calendar className="w-5 h-5 text-[#1679FF]" />
                      <span className="font-medium">
                        {pkg.validity_days
                          ? `Valid for ${pkg.validity_days} ${pkg.validity_days === 1 ? 'day' : 'days'}`
                          : `Valid for ${pkg.validity_months} ${pkg.validity_months === 1 ? 'month' : 'months'}`}
                      </span>
                    </div>
                  </div>

                  {pkg.description && (
                    <p className="text-sm text-white/60 pt-2">{pkg.description}</p>
                  )}

                  {pkg.features && pkg.features.length > 0 && (
                    <ul className="space-y-2 pt-2">
                      {pkg.features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="text-sm flex items-start text-white/80">
                          <span className="text-[#B8EC17] mr-2 mt-0.5">&check;</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link href="/book" className="block w-full mt-auto">
                    <Button
                      className="w-full group bg-[#1679FF] hover:bg-[#1679FF]/90 text-white font-conthrax"
                      size="lg"
                    >
                      Get Started
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --- Features Section ---
function FeaturesSection() {
  return (
    <section id="facilities-section" className="py-20 lg:py-32 bg-white">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#1679FF]/10 text-[#1679FF] px-4 py-2 rounded-full mb-4">
            World-Class Facilities
          </div>
          <h2 className="font-conthrax text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Everything You Need to Excel
          </h2>
          <p className="text-lg text-[#969696] max-w-2xl mx-auto">
            State-of-the-art amenities designed for the ultimate padel experience
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 group">
            <div className="w-14 h-14 bg-gradient-to-br from-[#1679FF] to-[#1679FF]/70 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <div className="text-3xl">&#127934;</div>
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">3 Premium Courts</h3>
            <p className="text-[#969696]">
              Professional-grade indoor courts with climate control for year-round play
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 group">
            <div className="w-14 h-14 bg-gradient-to-br from-[#B8EC17] to-[#B8EC17]/70 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform p-3">
              <Image
                src="/landing-pages/deem-padel/images/figma-icon-coaching.svg"
                alt="Coaching"
                width={32}
                height={32}
                className="w-full h-full brightness-0 invert"
              />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">Expert Coaching</h3>
            <p className="text-[#969696]">
              Professional trainers for private and group lessons at all levels
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 group">
            <div className="w-14 h-14 bg-gradient-to-br from-[#1679FF] to-[#1679FF]/70 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform p-3">
              <Image
                src="/landing-pages/deem-padel/images/figma-icon-clock.svg"
                alt="Clock"
                width={32}
                height={32}
                className="w-full h-full brightness-0 invert"
              />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">Extended Hours</h3>
            <p className="text-[#969696]">Open 10 AM - 2:30 AM daily</p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 group">
            <div className="w-14 h-14 bg-gradient-to-br from-[#B8EC17] to-[#B8EC17]/70 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform p-3">
              <Image
                src="/landing-pages/deem-padel/images/figma-icon-community.svg"
                alt="Community"
                width={32}
                height={32}
                className="w-full h-full brightness-0 invert"
              />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-3">Vibrant Community</h3>
            <p className="text-[#969696]">
              Join our welcoming community of padel enthusiasts and make new friends
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// --- Lazy Map Component ---
function LazyMap() {
  const [isLoaded, setIsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' },
    );

    if (mapRef.current) {
      observer.observe(mapRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={mapRef}
      className="bg-gray-300 rounded-lg flex items-center justify-center overflow-hidden h-[450px]"
    >
      {isLoaded ? (
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4305.669344783026!2d39.111237418893396!3d21.6041434148363!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c3dbd876fc6cb7%3A0xb292682143a6cafa!2sDeem%20Padel%20Club%20-%20INDOOR!5e0!3m2!1sen!2ssa!4v1760190501903!5m2!1sen!2ssa"
          width="100%"
          height="100%"
          style={{ border: 0, borderRadius: '10px' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Deem Padel Club Location"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Location Section ---
function LocationSection() {
  return (
    <section id="location-section" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <address className="not-italic">
            <p className="text-[#1679FF] text-lg lg:text-2xl font-semibold mb-3">
              Our Location
            </p>
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-[#1679FF] rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Deem Padel Club</h3>
                <p className="text-secondary-foreground text-sm mb-2">JEBB7594</p>
                <p className="text-secondary-foreground text-sm mb-2">
                  7594 Umm Al Mouameneen Zainab
                </p>
                <p className="text-secondary-foreground text-sm">
                  Jeddah, Saudi Arabia 23514
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-[#1679FF]" />
                <div>
                  <p className="font-medium text-foreground">Every Day</p>
                  <p className="text-secondary-foreground text-sm">10:00 AM - 2:30 AM</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-[#1679FF]" />
                <div>
                  <p className="font-medium text-foreground">Contact</p>
                  <a
                    href="tel:+966569339435"
                    className="text-[#1679FF] text-sm hover:underline"
                  >
                    +966 56 933 9435
                  </a>
                </div>
              </div>
            </div>
          </address>

          <LazyMap />
        </div>
      </div>
    </section>
  );
}

// --- Main Landing Page ---
export default function DeemPadelLandingPage() {
  return (
    <div className="min-h-screen bg-white font-conthrax overflow-x-hidden">
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
      `}</style>

      <HeroSection />
      <AboutSection />
      <PackagesSection />
      <FeaturesSection />
      <LocationSection />
    </div>
  );
}
